/** @jsxImportSource @emotion/react */
import { BlobProvider } from "@react-pdf/renderer";
import { Flex, Text } from "basis";
import { FC, memo, ReactNode } from "react";
import { SvgInline } from "../SvgInline/SvgInline";

function Anchor({ url, variant, fileName, children }) {
  const placeItems = variant === "button" ? "left center" : "left top";
  const height = variant === "button" ? 48 : undefined;
  return (
    <a
      css={{
        display: "inline-block",
        textDecoration: "none",
        height,
        ":hover": {
          textDecoration: "underline",
        },
      }}
      href={url}
      download={`${fileName}.pdf` || true}
    >
      <Flex height="100%" placeItems={placeItems}>
        <div css={{ display: "flex", flexShrink: 0 }}>
          <SvgInline name="icon-dl" size="20" />
        </div>
        <Text color="primary.blue.t100" margin="0 0 0 3">
          <strong>{children}</strong>
        </Text>
      </Flex>
    </a>
  );
}

interface DocumentProps {
  title?: string;
  author?: string;
  subject?: string;
  keywords?: string;
  creator?: string;
  producer?: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onRender?: () => any;
}

interface Props {
  url?: string;
  pdf?: React.ReactElement<DocumentProps>;
  fileName?: string;
  variant?: "button" | "text";
  children: ReactNode;
}
/*
  Without using React.memo, it throws:

    Uncaught Error: stream.push() after EOF

  See: https://github.com/diegomura/react-pdf/issues/420

  Important: Make sure that the pdf that is passed in here is memoized.
*/
const DownloadButton: FC<Props> = memo(
  ({ url, pdf, variant = "button", children, fileName }) => {
    if (pdf) {
      return (
        <BlobProvider document={pdf}>
          {({ url: pdfUrl }) => (
            <Anchor url={pdfUrl} variant={variant} fileName={fileName}>
              {children}
            </Anchor>
          )}
        </BlobProvider>
      );
    }

    return (
      <Anchor url={url} variant={variant} fileName={fileName}>
        {children}
      </Anchor>
    );
  },
);

export { DownloadButton };
